import type { IconProps } from '@phosphor-icons/react'
import type { ComponentType, ElementType, PropsWithChildren, ReactNode } from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import type { ButtonAsProps } from '../../polymorphic-props'
import { BaseButton } from '../button/button'

/** we use min-h-9 to ensure that the height of the chip is at least 36px,
 * otherwise icon only chips will have a smaller height then chips with an additional label */
const chipVariants = tv({
  base: 'font-base-copy flex min-h-9 w-fit max-w-full items-center gap-2 rounded-xl px-3 py-2 font-semibold',
  variants: {
    variant: {
      blank: 'text-primary hover:layer-bg-hover-light active:layer-bg-active-light bg-white',
      outlined:
        'border-primary/15 hover:layer-bg-hover-light active:layer-bg-active-light border bg-white',
      blue: 'bg-lightblue hover:layer-bg-hover-light active:layer-bg-active-light',
    },
    disabled: { true: 'text-primary/30 pointer-events-none' },
    active: {
      true: 'bg-primary hover:!layer-bg-hover-dark active:!layer-bg-active-dark text-white',
    },
    weight: {
      solid: 'shadow-elevation3',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
  },
  compoundVariants: [
    {
      disabled: true,
      variant: 'blue',
      class: 'bg-primary/15',
    },
  ],
  defaultVariants: { variant: 'blank', truncate: true },
})

export type ChipProps = VariantProps<typeof chipVariants> & {
  IconStart?: ComponentType<IconProps>
  IconEnd?: ComponentType<IconProps>
  itemEnd?: ReactNode
  itemStart?: ReactNode
}

export function Chip<TAs extends ElementType>(
  props: PropsWithChildren<ChipProps> & ButtonAsProps<TAs>
) {
  const {
    ref,
    IconStart,
    children,
    IconEnd,
    itemEnd,
    itemStart,
    variant,
    disabled,
    active,
    weight,
    truncate,
    ...rest
  } = props
  const classes = chipVariants({
    variant,
    disabled,
    active,
    weight,
    truncate,
  })
  const Start = itemStart || (IconStart && <IconStart className="size-4" weight="bold" />)

  return (
    <BaseButton className={classes} ref={ref} {...rest}>
      {Start}
      <div className="flex-1 truncate empty:hidden">{children}</div>
      {IconEnd && !disabled && <IconEnd className="size-4" weight="bold" />}
      {itemEnd && !disabled && itemEnd}
    </BaseButton>
  )
}
